import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useAcceptReturnRequest from "@babackapp/private-api-client/client/use-accept-return-request";
import useLineItemsState from "../../../refund/hooks/use-line-items-state-context";
import { useRefundLineItems } from "../../../refund/hooks/use-refund-line-items-context";
import { useRefundContext } from "../../../refund/use-refund-context";
import { useCurrentReturnRequest } from "../../../utils/current-return-request-context";
import { useLineItemsLoadingState } from "../../../utils/line-items-loading-state-context";

type AcceptReturnRequestParams = {
  onMutate: () => void;
  onSuccess: () => void;
  onError: () => void;
};

export const useSubmitAcceptReturnRequest = ({
  onMutate,
  onSuccess,
  onError,
}: AcceptReturnRequestParams) => {
  const { data: shop } = useCurrentShopSuspense();

  const currentReturnRequest = useCurrentReturnRequest();
  const { items: lineItems } = useRefundLineItems();
  const { lineItemsState } = useLineItemsState();
  const { setState: setLoadingState } = useLineItemsLoadingState();

  const {
    doNotChargeForRefund,
    doNotRefundInitialShipping,
    forceRefundInitialShipping,
    restockLocation,
    refundAmount,
    bypassStock,
    discountAmount,
    doNotCloseSbo,
  } = useRefundContext();

  /**
   * Update line items loading state before and after the mutation.
   */
  const setAcceptedLineItemsLoadingState = (
    items: { lineItemId: string }[],
    value: boolean,
  ) => {
    setLoadingState((prev) =>
      items.reduce(
        (acc, item) => {
          acc[item.lineItemId] = value;
          return acc;
        },
        { ...prev },
      ),
    );
  };

  const { mutateAsync: acceptReturnRequest } = useAcceptReturnRequest({
    shopId: shop.id,
    onMutate,
    onSuccess,
    onError,
  });

  return async function submit() {
    const acceptedLineItems = lineItems
      .filter(
        (lineItem) =>
          !!lineItem.resolution &&
          lineItemsState[lineItem.line_item_id].total.selectedQuantity > 0,
      )
      .map((lineItem) => ({
        lineItemId: lineItem.line_item_id,
        quantity: lineItemsState[lineItem.line_item_id].total.selectedQuantity,
        status: "ok",
      }));

    setAcceptedLineItemsLoadingState(acceptedLineItems, true);

    await acceptReturnRequest(
      {
        variables: {
          lineItems: acceptedLineItems,
          opt: {
            doNotChargeForRefund,
            doNotRefundInitialShipping,
            forceRefundInitialShipping,
            refundAmount,
            discountAmount,
            bypassStock: bypassStock,
            restockLocationId: restockLocation?.id || "",
            restockOnReturn: !!restockLocation,
            doNotCloseSbo,
          },
        },
        returnRequestId: currentReturnRequest.id,
      },
      {
        onSettled: () =>
          setAcceptedLineItemsLoadingState(acceptedLineItems, false),
      },
    );
  };
};
